import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { AgentBankPaymentReviewApiService } from 'src/app/services/agent-bank-payment-review-api.service';
import { BankPaymentDocumentService, PaymentPersistError, PaymentPreconditionError } from 'src/app/services/bank-payment-document.service';
import { AgentBankPaymentReviewItem } from 'src/app/shared/agent-bank-payment-review';
import { Document } from 'src/app/shared/document';
import {
  ALLOCATION_TOLERANCE, AllocationValidation, defaultAmount, greedyAllocate, isShippedUnpaid, orderForExecution,
  paymentPercent, rankCandidates, validateAllocation
} from 'src/app/shared/bank-payment-allocation';
import { SplitEvent, runSplitSequence } from 'src/app/shared/bank-payment-split-runner';
import { round2 } from 'src/app/shared/round';

/** `queued`: will be / is being created. `failed`: nothing was created (a safety
 *  condition failed, or the error left no payment behind). `needsCheck`: a
 *  payment may or may not exist, so it is never retried automatically. */
export type SplitLineStatus = 'idle' | 'queued' | 'created' | 'failed' | 'needsCheck' | 'notAttempted';

/** One invoice row in the dialog. */
export interface SplitLine {
  /** The invoice DOCID. */
  key: number;
  documentNumber: string;
  documentDate: string | Date | null;
  clientName: string;
  /** The invoice's own document currency (e.g. "CUSD"): informational only. `null` when unknown or added by hand. */
  currencyCode: string | null;
  outstanding: number;
  /** The invoice's full sum (`DOCSUM`), main currency; the base of the percent column. `null` when unknown. */
  documentSum: number | null;
  /** Money already shipped (`SHIPSUM`), main currency. `null` when unknown (rows added by hand). */
  shippedSum: number | null;
  /** Shipped worth more than has been paid -- ranked ahead of unshipped invoices. */
  shippedUnpaid: boolean;
  isSuggested: boolean;
  /** Added through the document picker rather than the candidate list. */
  manual: boolean;
  checked: boolean;
  amount: number | null;
  status: SplitLineStatus;
  paymentDocumentId: number | null;
  message: string | null;
}

/** Emitted when the payment was saved but recording the bank row failed. */
export interface SplitRecoveryEvent {
  row: AgentBankPaymentReviewItem;
  paymentDocumentId: number;
  invoiceDocumentId: number;
}

/**
 * "Розподіл платежу по рахунках": one bank transaction paying several invoices.
 * Opened from a queue row's split-button menu in `AgentBankPaymentReviewComponent`.
 * Spec: `docs/PROMPT_agent-bank-payment-review-multi-invoice.md`.
 *
 * **Input helper, not a ledger (owner, 2026-09-29).** Nothing about the split is
 * stored: the bank row records only the FIRST payment's DOCID (the existing
 * `PATCH /:id/process`), and payments 2..N are ordinary `DOCTYPE=9` documents
 * findable through their invoices like manually entered ones. Once the dialog is
 * closed nothing remembers an unfinished allocation -- the row is already
 * processed and any still-open invoice simply shows its debt in the normal screens.
 *
 * **Run order** (see `runSplitSequence`): one payment at a time, the bank row
 * recorded right after the first one saves and before any further one, stop at the
 * first failure, nothing retried automatically or rolled back. Auto-create only
 * for now; "review each in the payment dialog" is deferred.
 *
 * The API assigns new document ids as MAX(DOCID)+1, so the run is strictly
 * sequential, and the dialog cannot be closed while it runs.
 */
@Component({
  selector: 'app-agent-bank-payment-split',
  templateUrl: './agent-bank-payment-split.component.html',
  styleUrls: ['./agent-bank-payment-split.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class AgentBankPaymentSplitComponent implements OnChanges, OnDestroy {

  @Input() display: boolean = false;
  /** The pending bank transaction being split. */
  @Input() row: AgentBankPaymentReviewItem = null;

  @Output() displayChange = new EventEmitter<boolean>();
  /** The bank row was recorded as processed (id = `AgentBankTransactions.id`); the queue should drop it. */
  @Output() rowProcessed = new EventEmitter<number>();
  /** A payment was saved but the bank row could not be recorded -- the queue shows its recovery banner. */
  @Output() recovery = new EventEmitter<SplitRecoveryEvent>();

  lines: SplitLine[] = [];
  phase: 'edit' | 'running' | 'result' = 'edit';
  loading: boolean = false;
  loadError: string | null = null;
  validation: AllocationValidation = validateAllocation([], 0);

  /** Bumped to re-create the document picker after each pick (it keeps showing the last pick otherwise). */
  pickerKey: number = 0;

  /** True once `PATCH /:id/process` has succeeded for this row in this dialog session. */
  private rowRecorded: boolean = false;
  /** True when the first payment was saved but recording it failed: nothing more may be created until that is resolved. */
  private recordingFailed: boolean = false;
  private runSub: Subscription = null;
  private loadSub: Subscription = null;
  /** Base (accounting) currency `DOCSUM` is stated in, resolved on open. */
  baseCurrencyCode: string | null = null;
  /** Why nothing here can be paid: the transaction is not in the base currency, or the base currency is unknown. */
  currencyBlockMessage: string | null = null;
  /** Completed in ngOnDestroy: cancels the candidate load and the base-currency lookup still in flight. */
  private destroy$ = new Subject<void>();

  constructor(
    private api: AgentBankPaymentReviewApiService,
    private paymentDocs: BankPaymentDocumentService,
    private messageService: MessageService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes.display || changes.row) && this.display && this.row) {
      this.init();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.runSub) {
      this.runSub.unsubscribe();
    }
  }

  // -------------------------------------------------------------------------
  // Loading + prefill
  // -------------------------------------------------------------------------

  private init() {
    this.baseCurrencyCode = null;
    this.currencyBlockMessage = null;
    this.lines = [];
    this.phase = 'edit';
    this.loadError = null;
    this.rowRecorded = false;
    this.recordingFailed = false;
    this.pickerKey++;
    this.revalidate();
    this.loading = true;

    // A re-init (dialog reopened for another row) must not let the earlier load land on this one.
    if (this.loadSub) {
      this.loadSub.unsubscribe();
    }
    // `DOCSUM`/`PAYDSUM` are stated in the base currency whatever an invoice's own currency is
    // ("CUSD" etc. is a display denomination), so the only currency question is whether the
    // *transaction* is in the base currency. An unresolved base currency blocks, never passes.
    this.loadSub = forkJoin({
      candidates: this.api.getCandidateInvoices(this.row.id),
      baseCurrency: this.paymentDocs.getBaseCurrencyCode().pipe(catchError(() => of(null as string | null))),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ candidates, baseCurrency }) => {
        this.baseCurrencyCode = baseCurrency;
        this.currencyBlockMessage = this.currencyBlockReason(this.row.currency_code, baseCurrency);
        // Same order the pre-tick uses: suggestion, shipped-but-unpaid, the rest (each oldest first).
        this.lines = rankCandidates((candidates || []).map(c => ({
          key: c.document_id,
          documentNumber: c.document_number,
          documentDate: c.document_date,
          clientName: c.client_name,
          currencyCode: c.currency_code,
          outstanding: c.outstanding,
          documentSum: c.document_sum,
          shippedSum: c.shipped_sum ?? null,
          shippedUnpaid: isShippedUnpaid(c.shipped_sum, c.paid_sum),
          isSuggested: c.is_suggested,
          manual: false,
          checked: false,
          amount: null,
          status: 'idle' as SplitLineStatus,
          paymentDocumentId: null,
          message: null,
        })));

        const prefill = greedyAllocate(
          this.lines.map(l => ({
            key: l.key, outstanding: l.outstanding, isSuggested: l.isSuggested, shippedUnpaid: l.shippedUnpaid,
            selectable: !this.currencyBlockMessage
          })),
          this.row.amount_decimal
        );
        for (const line of this.lines) {
          const amount = prefill.get(line.key);
          if (amount !== undefined) {
            line.checked = true;
            line.amount = amount;
          }
        }
        this.revalidate();
        this.loading = false;
      },
      error: err => {
        console.error('*** AgentBankPaymentSplitComponent getCandidateInvoices error:', err);
        this.loadError = 'Не вдалося завантажити рахунки контрагента. Ви можете додати рахунок вручну.';
        this.loading = false;
      }
    });
  }

  private currencyBlockReason(bank: string | null | undefined, base: string | null): string | null {
    if (!base) {
      return 'Не вдалося визначити валюту обліку -- розподіл недоступний.';
    }
    const bankCode = (bank || '').trim().toUpperCase();
    if (bankCode !== base) {
      return 'Валюта транзакції (' + (bank || '?') + ') відрізняється від валюти обліку (' + base + ') -- тут її не можна розподілити.';
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // Editing
  // -------------------------------------------------------------------------

  revalidate() {
    this.validation = validateAllocation(
      this.lines.map(l => ({ checked: l.checked, amount: l.amount, outstanding: l.outstanding })),
      this.row ? this.row.amount_decimal : 0
    );
  }

  /** A ticked invoice with no amount yet starts with as much of its debt as the unallocated remainder covers. */
  onCheckChange(line: SplitLine) {
    if (line.checked && (line.amount == null || !(line.amount > 0))) {
      const others = this.lines
        .filter(l => l !== line && l.checked)
        .reduce((sum, l) => round2(sum + (l.amount || 0)), 0);
      const amount = defaultAmount(line.outstanding, round2(this.row.amount_decimal - others));
      line.amount = amount > 0 ? amount : null;
    }
    this.revalidate();
  }

  /** Amounts are kept in whole cents: the validator adds cents, and the payment is posted with exactly this value. */
  onAmountChange(line: SplitLine, amount: number | null) {
    line.amount = amount == null ? null : round2(amount);
    this.revalidate();
  }

  /** The tooltip of the disabled run button: the first thing wrong with the split. */
  get firstProblem(): string | null {
    return this.validation.totalError || this.validation.lineErrors.find(e => !!e) || null;
  }

  /** Percent of the invoice's full sum that this line's payment covers, one decimal; `null` when unticked or unknown. */
  percentOf(line: SplitLine): number | null {
    return line.checked ? paymentPercent(line.amount, line.documentSum) : null;
  }

  /** `ok` / `under` / `over`, for colouring the "allocated of total" line. */
  get totalState(): 'ok' | 'under' | 'over' {
    // Exact cents, so no tolerance: even one cent over is over (see validateAllocation).
    if (this.validation.remaining < 0) {
      return 'over';
    }
    return this.validation.remaining > 0 ? 'under' : 'ok';
  }

  /** Adds an invoice outside the candidate list, picked with the shared document selector. Unticked -- the manager decides. */
  onManualInvoice(doc: Document | null) {
    if (!doc) {
      return;
    }
    const refuse = (detail: string) => {
      this.messageService.add({ severity: 'warn', summary: 'Рахунок не додано', detail });
      this.pickerKey++;
    };

    if (this.lines.some(l => l.key === doc.DOCID)) {
      refuse('Цей рахунок вже є в списку.');
      return;
    }
    if (doc.DOCTYPE !== 1 || !doc.DOCACTIVE) {
      refuse('Це не активний рахунок.');
      return;
    }
    // `PATCH /:id/process` only accepts an invoice issued by the transaction's own entity,
    // so refuse a foreign one now rather than after a payment has been created.
    if ((doc.DOCLCOCLIID ?? doc.DOCMANCLIID) !== this.row.our_cliid) {
      refuse('Рахунок виписано іншою компанією, ніж та, що отримала платіж.');
      return;
    }
    const outstanding = round2((doc.DOCSUM ?? 0) - (doc.DOCPAIDSUM ?? 0));
    if (doc.DOCSUM == null || doc.DOCPAIDSUM == null || !(outstanding > ALLOCATION_TOLERANCE)) {
      refuse('За цим рахунком немає боргу.');
      return;
    }

    this.lines = [...this.lines, {
      key: doc.DOCID,
      documentNumber: (doc.DOCNUMBER || '').toString().trim(),
      documentDate: doc.DOCDATE || null,
      // `Document` carries neither a customer name nor a currency code, and neither
      // matters here: the picker has just shown the document itself.
      clientName: '',
      currencyCode: null,
      outstanding,
      documentSum: doc.DOCSUM ?? null,
      // The picker's `Document` carries no shipped figure (`DOCSHIPSUM` is never populated), so a
      // hand-added row shows none and is not ranked as shipped-but-unpaid.
      shippedSum: null,
      shippedUnpaid: false,
      isSuggested: false,
      manual: true,
      checked: false,
      amount: null,
      status: 'idle',
      paymentDocumentId: null,
      message: null,
    }];
    this.pickerKey++;
    this.revalidate();
  }

  // -------------------------------------------------------------------------
  // Running
  // -------------------------------------------------------------------------

  get canRun(): boolean {
    return this.phase === 'edit' && this.validation.ok && !this.loading && !this.currencyBlockMessage;
  }

  onRun() {
    if (!this.canRun) {
      return;
    }
    const planned = orderForExecution(this.lines.filter(l => l.checked));
    for (const line of this.lines) {
      line.status = planned.includes(line) ? 'queued' : 'idle';
      line.message = null;
    }
    this.execute(planned);
  }

  /** Lines a "retry the rest" would run: failed cleanly or never reached. Never a created one,
   *  never one whose outcome is uncertain (`needsCheck`). */
  get retryable(): SplitLine[] {
    return this.recordingFailed ? [] : this.lines.filter(l => l.status === 'failed' || l.status === 'notAttempted');
  }

  onRetryRemaining() {
    const remaining = orderForExecution(this.retryable);
    if (this.phase !== 'result' || remaining.length === 0) {
      return;
    }
    for (const line of remaining) {
      line.status = 'queued';
      line.message = null;
    }
    this.execute(remaining);
  }

  private execute(planned: SplitLine[]) {
    const row = this.row;
    this.phase = 'running';

    this.runSub = runSplitSequence(planned, {
      create: line => this.paymentDocs.createPaymentForInvoice({
        invoiceId: line.key,
        amount: line.amount,
        bankCurrencyCode: row.currency_code,
        bookingDate: row.booking_date,
      }),
      record: (line, paymentId) => this.api.processTransaction(row.id, paymentId, line.key),
    }, this.rowRecorded).subscribe({
      next: event => this.onEvent(planned, event),
      complete: () => this.onRunComplete(planned),
    });
  }

  private onEvent(planned: SplitLine[], event: SplitEvent) {
    const line = planned[event.index];
    switch (event.type) {
      case 'created':
        line.status = 'created';
        line.paymentDocumentId = event.paymentId;
        break;

      case 'recorded':
        this.rowRecorded = true;
        this.rowProcessed.emit(this.row.id);
        break;

      case 'recordFailed':
        // The payment exists; only recording it against the bank row failed. Hand it to the
        // queue's own recovery banner (retry that status update with this same DOCID).
        console.error('*** AgentBankPaymentSplitComponent: process() failed after a real save:', event.error);
        line.status = 'created';
        line.paymentDocumentId = event.paymentId;
        line.message = 'Платіж створено, але не вдалося зафіксувати транзакцію. Після закриття вікна повторіть фіксацію у верхній панелі.';
        this.recordingFailed = true;
        this.recovery.emit({ row: this.row, paymentDocumentId: event.paymentId, invoiceDocumentId: line.key });
        break;

      case 'failed':
        this.describeFailure(line, event.error);
        break;
    }
  }

  private describeFailure(line: SplitLine, error: unknown) {
    if (error instanceof PaymentPreconditionError) {
      line.status = 'failed';
      line.message = 'Не створено: ' + error.message;
    } else if (error instanceof PaymentPersistError && error.stage === 'items') {
      console.error('*** AgentBankPaymentSplitComponent: header saved, item failed:', error);
      line.status = 'needsCheck';
      line.paymentDocumentId = error.documentId;
      line.message = 'Створено платіж №' + error.documentId + ' без рядка платежу -- перевірте цей документ у списку документів.';
    } else if (error instanceof PaymentPersistError) {
      console.error('*** AgentBankPaymentSplitComponent: header save failed or unknown:', error);
      line.status = 'needsCheck';
      line.message = 'Не вдалося підтвердити створення платежу -- перевірте список документів, перш ніж повторювати.';
    } else {
      console.error('*** AgentBankPaymentSplitComponent: unexpected error:', error);
      line.status = 'failed';
      line.message = 'Не створено: не вдалося підготувати платіж.';
    }
  }

  private onRunComplete(planned: SplitLine[]) {
    // Whatever the run never reached.
    for (const line of planned) {
      if (line.status === 'queued') {
        line.status = 'notAttempted';
        line.message = 'Не виконано';
      }
    }
    this.phase = 'result';
    this.runSub = null;

    const created = this.lines.filter(l => l.status === 'created');
    // Judge ALL lines, not just the ones this run touched: a "retry the rest" that succeeds
    // must not close the dialog over an earlier line that still needs checking or a failure.
    const unresolved = this.lines.some(l => l.status === 'failed' || l.status === 'needsCheck' || l.status === 'notAttempted');
    const allDone = !unresolved && created.length > 0 && this.rowRecorded && !this.recordingFailed;
    if (allDone) {
      const numbers = created.map(l => '№' + l.paymentDocumentId).join(', ');
      this.messageService.add({
        severity: 'success', summary: 'Готово',
        detail: created.length === 1 ? 'Платіж ' + numbers + ' збережено.' : 'Створено платежів: ' + created.length + ' (' + numbers + ').'
      });
      this.close();
    }
  }

  // -------------------------------------------------------------------------
  // Closing
  // -------------------------------------------------------------------------

  /** Never while a run is in progress. */
  close() {
    if (this.phase === 'running') {
      return;
    }
    this.displayChange.emit(false);
  }

  /** The dialog's own X and Escape. (The mask is deliberately not dismissable -- PrimeNG's
   *  `dismissableMask` defaults to false and is left off, so a stray click outside cannot throw
   *  away a half-entered allocation.) `[visible]` is one-way, so PrimeNG only emits
   *  `visibleChange(false)` and never hides by itself -- `(onHide)` fires after a hide has
   *  already happened, which never came, so nothing listened and the X did nothing. */
  onVisibleChange(visible: boolean) {
    if (!visible) {
      this.close();
    }
  }
}
